import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import crypto from "crypto-js";

// 安全提示：实际生产环境应使用环境变量存储敏感信息
const API_KEY = process.env.REACT_APP_OK_ACCESS_KEY;
const SECRET_KEY = process.env.REACT_APP_OK_SECRET_KEY;
const PASSPHRASE = process.env.REACT_APP_OK_ACCESS_PASSPHRASE;
const CORS_PROXY = "https://cors-proxy.voxon91612.workers.dev/?url=";
const BASE_URL = "https://web3.okx.com";

const TOKEN_SYMBOLS = {
  "0xC71B5F631354BE6853eFe9C3Ab6b9590F8302e81": "ZKJ",
  "0xe6df05ce8c8301223373cf5b969afcb1498c5528": "KOGE",
  "0xa0c56a8c0692bD10B3fA8f8bA79Cf5332B7107F9": "MERL",
  "0xb4357054c3dA8D46eD642383F03139aC7f090343": "PORT3",
};

const TARGET_TOKENS = [
  "0xC71B5F631354BE6853eFe9C3Ab6b9590F8302e81",
  "0xe6df05ce8c8301223373cf5b969afcb1498c5528",
  "0xa0c56a8c0692bD10B3fA8f8bA79Cf5332B7107F9",
  "0xb4357054c3dA8D46eD642383F03139aC7f090343",
];

// 生成签名
const generateSignature = (timestamp, method, requestPath, body = "") => {
  const prehash = timestamp + method + requestPath + body;
  return crypto.enc.Base64.stringify(crypto.HmacSHA256(prehash, SECRET_KEY));
};

const TokenAnalyzer = () => {
  const [tokensData, setTokensData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(Date.now());

  // 带签名的API请求（使用useCallback优化）
  const signedRequest = useCallback(async (config) => {
    const timestamp = new Date().toISOString();
    const queryString = new URLSearchParams(config.params).toString();
    const requestPath = `/api/v5/${config.url}${
      queryString ? `?${queryString}` : ""
    }`;

    const signature = generateSignature(
      timestamp,
      config.method.toUpperCase(),
      requestPath
    );

    try {
      const response = await axios({
        ...config,
        baseURL: CORS_PROXY + BASE_URL,
        headers: {
          "OK-ACCESS-KEY": API_KEY,
          "OK-ACCESS-SIGN": signature,
          "OK-ACCESS-TIMESTAMP": timestamp,
          "OK-ACCESS-PASSPHRASE": PASSPHRASE,
        },
        timeout: 10000, // 添加超时控制
      });
      return response.data.data;
    } catch (error) {
      console.error("API请求失败:", error.response?.data || error.message);
      throw error; // 抛出错误以便后续处理
    }
  }, []);

  // 改进后的波动率计算（基于5分钟数据）
  const calculateVolatility = useCallback((candles) => {
    if (!candles || candles.length < 5) return 0;

    const closes = candles.map((c) => parseFloat(c[4]));
    const max = Math.max(...closes);
    const min = Math.min(...closes);
    const avg = closes.reduce((a, b) => a + b, 0) / closes.length;

    // 波动率 = (最大-最小)/平均 × 100%
    return ((max - min) / avg) * 100;
  }, []);

  // 获取K线数据（添加重试机制）
  const fetchTokenData = useCallback(
    async (instId) => {
      const params = {
        chainIndex: 56,
        tokenContractAddress: instId,
        bar: "1m",
        limit: 5,
      };

      try {
        const data = await signedRequest({
          method: "get",
          url: "/api/v5/dex/market/candles",
          params,
        });

        return {
          symbol: TOKEN_SYMBOLS[instId] || instId, // 优先使用映射表,
          address: instId, // 保留原始地址
          volatility: calculateVolatility(data),
          volumeUSD: data?.[0]?.[6] || 0,
          price: data?.[0]?.[4] || 0, // 添加实时价格
        };
      } catch (error) {
        console.error(`代币 ${instId} 数据获取失败:`, error);
        return { symbol: TOKEN_SYMBOLS[instId] || instId, error: true };
      }
    },
    [signedRequest, calculateVolatility]
  );

  // 数据加载逻辑（添加自动刷新）
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const results = await Promise.all(TARGET_TOKENS.map(fetchTokenData));

      const validData = results
        .filter((item) => !item.error)
        .sort((a, b) => b.volumeUSD - a.volumeUSD) // 按交易量排序
        .sort((a, b) => a.volatility - b.volatility); // 优先按波动率排序

      setTokensData(validData);
      setLastUpdate(Date.now());
    } catch (error) {
      console.error("全局数据加载失败:", error);
    } finally {
      setLoading(false);
    }
  }, [fetchTokenData]);

  useEffect(() => {
    // 初始加载
    loadData();

    // 设置定时刷新（每30秒）
    const interval = setInterval(loadData, 60 * 1000);

    // 清理函数
    return () => clearInterval(interval);
  }, [loadData]);

  return (
    <div className="container">
      <h1>代币波动率（最近5分钟）</h1>
      <div className="update-time">
        更新时间: {new Date(lastUpdate).toLocaleTimeString()}
      </div>

      {loading ? (
        <div className="loader">加载中...</div>
      ) : (
        <>
          {/* 桌面端表格 */}
          <table className="desktop-table">
            <thead>
              <tr>
                <th>代币</th>
                <th>价格</th>
                <th>波动率</th>
                <th>交易量</th>
              </tr>
            </thead>
            <tbody>
              {tokensData.map((token) => (
                <tr key={token.address}>
                  <td>
                    <div className="token-info">
                      <span className="symbol">{token.symbol}</span>
                      <div className="address">
                        {token.address.slice(0, 6)}...{token.address.slice(-4)}
                      </div>
                    </div>
                  </td>
                  <td>${Number(token.price).toFixed(4)}</td>
                  <td
                    className={`volatility ${getVolatilityClass(
                      token.volatility
                    )}`}
                  >
                    {token.volatility.toFixed(2)}%
                  </td>
                  <td>${Number(token.volumeUSD).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* 移动端列表 */}
          <div className="mobile-list">
            {tokensData.map((token) => (
              <div key={token.address} className="token-card">
                <div className="header">
                  <span className="symbol">{token.symbol}</span>
                  <span className="price">
                    ${Number(token.price).toFixed(4)}
                  </span>
                </div>
                <div className="details">
                  <div className="volatility-box">
                    <span>波动率</span>
                    <span className={getVolatilityClass(token.volatility)}>
                      {token.volatility.toFixed(2)}%
                    </span>
                  </div>
                  <div className="volume-box">
                    <span>交易量</span>
                    <span>${(token.volumeUSD / 1000).toFixed(1)}K</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      <style>{`
        .container {
          padding: 20px;
          max-width: 1200px;
          margin: 0 auto;
        }

        /* 桌面端样式 */
        .desktop-table {
          width: 100%;
          border-collapse: collapse;
          display: none;
        }
        
        .desktop-table th, 
        .desktop-table td {
          padding: 12px;
          border-bottom: 1px solid #eee;
          text-align: center;
        }

        .token-info {
          display: flex;
          flex-direction: column;
        }
        
        .symbol {
          font-weight: bold;
          color: #1890ff;
        }
        
        .name {
          font-size: 0.8em;
          color: #666;
        }

        /* 移动端样式 */
        .mobile-list {
          display: block;
          width: 100%;
        }

        .token-card {
          background: white;
          border-radius: 8px;
          padding: 15px;
          margin-bottom: 10px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }

        .header {
          display: flex;
          justify-content: space-between;
          margin-bottom: 10px;
        }

        .price {
          font-weight: bold;
          color: #333;
        }

        .details {
          display: flex;
          justify-content: space-between;
        }

        .volatility-box, 
        .volume-box {
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        /* 响应式适配 */
        @media (min-width: 768px) {
          .desktop-table { display: table; }
          .mobile-list { display: none; }
        }

        @media (max-width: 767px) {
          .container {
            padding: 10px;
          }
          
          .update-time {
            font-size: 0.9em;
          }
        }

        /* 波动率颜色 */
        .low { color: #52c41a; }
        .medium { color: #faad14; }
        .high { color: #ff4d4f; }
      `}</style>
    </div>
  );
};
// 辅助函数
const getVolatilityColor = (volatility) => {
  if (volatility > 5) return "#ff4d4f";
  if (volatility > 2) return "#faad14";
  return "#52c41a";
};
// 辅助函数
const getVolatilityClass = (value) => {
  if (value < 0.1) return "low";
  if (value < 1) return "medium";
  return "high";
};

// 添加样式建议
const styles = `
.token-analysis {
  max-width: 800px;
  margin: 20px auto;
  padding: 20px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.update-time {
  text-align: right;
  color: #666;
  margin-bottom: 12px;
  font-size: 0.9em;
}

table {
  width: 100%;
  border-collapse: collapse;
}

th, td {
  padding: 12px 15px;
  border-bottom: 1px solid #e8e8e8;
  text-align: right;
}

th:first-child, td:first-child {
  text-align: left;
}

.loading {
  padding: 20px;
  text-align: center;
  color: #666;
}

// 新增样式

.token-info {
  display: flex;
  flex-direction: column;
}

.symbol {
  font-weight: 600;
  color: #1890ff;
}

.address {
  font-size: 0.8em;
  color: #666;
}
// 新增样式（关键移动端适配）

/* 通用样式 */
.responsive-table {
  overflow-x: auto;
}

/* 桌面端显示 */
@media (min-width: 768px) {
  .mobile-view { display: none; }
  table {
    width: 100%;
    display: table;
  }
}

/* 移动端显示 */
@media (max-width: 767px) {
  .desktop-view { display: none; }
  
  .mobile-view {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .token-card {
    background: #fff;
    border-radius: 8px;
    padding: 16px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  }

  .card-row {
    display: flex;
    justify-content: space-between;
    margin-bottom: 8px;
    
    &:last-child {
      margin-bottom: 0;
    }
  }

  .symbol {
    font-weight: 600;
    color: #1890ff;
    font-size: 16px;
  }

  .price {
    font-size: 14px;
    color: #333;
  }

  .volatility {
    font-size: 14px;
    min-width: 60px;
    text-align: right;
  }

  .volume {
    font-size: 12px;
    color: #666;
  }
}

/* 通用移动优化 */
@media (max-width: 480px) {
  .token-analysis {
    padding: 10px;
  }

  h2 {
    font-size: 1.2rem;
  }

  .update-time {
    font-size: 0.8em;
  }
}
`;

// 注入样式
document.head.insertAdjacentHTML("beforeend", `<style>${styles}</style>`);

export default TokenAnalyzer;
